import { useEffect, useState, useMemo } from "react";
import {
  Spinner,
  Alert,
  Button,
  Card,
  Modal,
  Row,
  Col,
  Pagination,
  Container,
  Badge,
  Form,
} from "react-bootstrap";

const JobApplications = () => {
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPost, setFilterPost] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const API_URL = "https://radheyelectrical.com/api/applications.php";
  const REJECT_API_URL = "https://radheyelectrical.com/api/applications.php";

  useEffect(() => {
    fetchAllApplications();
  }, []);

  const fetchAllApplications = async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      // Default Sort: Newest First
      const sorted = data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setAllApplications(sorted);
    } catch (err) {
      setError("Critical: Could not retrieve application database.");
    } finally {
      setLoading(false);
    }
  };

  const positions = useMemo(() => {
    return [...new Set(allApplications.map((app) => app.position))];
  }, [allApplications]);

  const { currentItems, totalPages, filteredCount } = useMemo(() => {
    const filtered = allApplications.filter((app) => {
      const matchesSearch =
        app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPost = filterPost === "" || app.position === filterPost;
      return matchesSearch && matchesPost;
    });

    const lastIdx = currentPage * itemsPerPage;
    const firstIdx = lastIdx - itemsPerPage;

    return {
      currentItems: filtered.slice(firstIdx, lastIdx),
      totalPages: Math.ceil(filtered.length / itemsPerPage) || 1,
      filteredCount: filtered.length,
    };
  }, [allApplications, searchTerm, filterPost, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // FIXED: Now expects ID, Name, and Email
  const handleReject = async (e, id, name, email) => {
    e.stopPropagation(); // Prevents the modal from opening when clicking the button
    if (!window.confirm(`Confirm: Reject ${name} and send email?`)) return;

    try {
      const res = await fetch(REJECT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Added action flag and ID
        body: JSON.stringify({ action: "reject", id, name, email }),
      });

      const data = await res.json();

      if (res.ok && data.status === "success") {
        alert("Applicant rejected and email dispatched.");
        // Update local state to reflect the change immediately
        setAllApplications((prevApps) =>
          prevApps.map((app) =>
            app.id === id ? { ...app, status: "rejected" } : app
          )
        );
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert("API Error: Rejection failed to process.");
    }
  };

  const styles = {
    wrapper: {
      minHeight: "100vh",
      paddingBottom: "50px",
    },
    glassCard: {
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: "15px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
      transition: "transform 0.2s ease",
      cursor: "pointer",
    },
  };

  // Helper for Badge colors based on status
  const getBadgeColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "warning";
      case "accepted":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <div style={styles.wrapper}>
      <div
        className="border-bottom mb-4 py-4 shadow-sm sticky-top"
        style={{ background: "#fff", zIndex: 100 }}
      >
        <Container>
          <Row className="align-items-center gy-3">
            <Col lg={4}>
              <small className="text-muted">
                {filteredCount} matches found
              </small>
            </Col>
            <Col lg={4}>
              <Form.Control
                type="text"
                placeholder="Search name or email..."
                className="rounded-pill"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col lg={4}>
              <Form.Select
                className="rounded-pill"
                value={filterPost}
                onChange={(e) => {
                  setFilterPost(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Positions</option>
                {positions.map((pos) => (
                  <option key={pos} value={pos}>
                    {pos}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        {loading && (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="g-3">
          {!loading &&
            currentItems.map((app) => (
              <Col md={12} key={app.id}>
                <Card
                  style={styles.glassCard}
                  className="card-hover-effect border-0"
                  onClick={() => {
                    setSelectedApp(app);
                    setShowModal(true);
                  }}
                >
                  <Card.Body className="p-3">
                    <Row className="align-items-center">
                      <Col md={3}>
                        <div className="fw-bold text-dark">{app.full_name}</div>
                        <div className="text-muted small">{app.email}</div>
                      </Col>
                      <Col md={3}>
                        <Badge bg="light" text="dark" className="border">
                          {app.position}
                        </Badge>
                      </Col>
                      <Col md={2} className="text-muted small">
                        {new Date(app.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Col>
                      <Col md={2}>
                        <Badge bg={getBadgeColor(app.status)} pill>
                          {app.status.charAt(0).toUpperCase() +
                            app.status.slice(1)}
                        </Badge>
                      </Col>
                      <Col md={2} className="text-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="rounded-pill px-3"
                          disabled={app.status.toLowerCase() === "rejected"}
                          onClick={(e) =>
                            handleReject(e, app.id, app.full_name, app.email)
                          }
                        >
                          {app.status.toLowerCase() === "rejected"
                            ? "Rejected"
                            : "Reject"}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))}
        </Row>

        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-center mt-5">
            <Pagination>
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              />
              <Pagination.Item active>
                {currentPage} of {totalPages}
              </Pagination.Item>
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </Pagination>
          </div>
        )}
      </Container>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApp && (
            <Row>
              <Col md={8}>
                <h5>{selectedApp.full_name}</h5>
                <p className="text-primary">{selectedApp.position}</p>
                <hr />
                <h6>Experience / Cover Letter</h6>
                <p
                  className="bg-light p-3 rounded"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {selectedApp.experience || "No details provided."}
                </p>
              </Col>
              <Col md={4} className="bg-light p-3 rounded">
                <p>
                  <strong>Phone:</strong> {selectedApp.phone}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <Badge bg={getBadgeColor(selectedApp.status)}>
                    {selectedApp.status.toUpperCase()}
                  </Badge>
                </p>
                <p>
                  <strong>Applied On:</strong>{" "}
                  {new Date(selectedApp.created_at).toLocaleString()}
                </p>
                {selectedApp.resume && (
                  <Button
                    variant="dark"
                    className="w-100 mt-2"
                    href={`https://radheyelectrical.com/api${selectedApp.resume}`}
                    target="_blank"
                  >
                    View CV / Resume
                  </Button>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default JobApplications;
